import Collaborate16 from '@carbon/icons-react/es/collaborate/16';
import Money16 from '@carbon/icons-react/es/money/16';
import Task16 from '@carbon/icons-react/es/task/16';
import UserMultiple16 from '@carbon/icons-react/es/user--multiple/16';
import Animate from 'rc-animate';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { Step, StepObject, Steps, Wizard } from 'react-albus';
import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { SimpleSearchFilter } from 'api/dashboard/widget';
import { ProjectId, Team, User, UserType } from 'api/models';
import { VisibilityType, ProjectFinancialsContractType } from 'api/project';
import { Field } from 'api/tasks';
import { ReactComponent as BasicInfo16 } from 'assets/svgs/basic-info.svg';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { RootState } from 'data';
import { ProjectTemplateApi } from 'data/templates/projects';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import {
  useBulkUserCreateMutation,
  useCreateCompanyMutation,
  useCreateUserMutation,
  useProjectFieldsQuery,
} from 'queries';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { useGetAllActivePartnerCompanies } from 'queries/partner';
import { getTeamsInAccountQueryKey } from 'queries/placeholder';
import { useCreateProjectMutation } from 'queries/project';
import { useProjectFieldSectionsQuery } from 'queries/project-field-sections';
import { useRolesWithPlaceholderQuery } from 'queries/roles';
import { useTemplateDueDateQuery } from 'queries/templates/common';
import { logger } from 'services';
import { added as addUser } from 'stores/users';
import { byFieldName } from 'utils/by-field-name';
import { queueMacrotask } from 'utils/macrotask';
import { userOption } from 'utils/project-fields/construct-user-option';
import { UserOption } from 'utils/project-fields/types';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { getProjectPlanUrl } from 'utils/urls';
import { companyOption } from './company-creatable-select';
import {
  CustomerTeamStepFormValues,
  CustomerTeamStep,
} from './customer-team-step';
import {
  FinancialInfoStep,
  FinancialInfoStepFormValues,
} from './financials-step';
import { Footer } from './footer';
import { ProjectCreationLoader } from './loader';
import { Preview } from './preview';
import {
  anyTemplateHasBudgetedHoursConfig,
  ProjectFieldsStep,
  ProjectFieldsStepFormValues,
} from './project-fields-step';
import { useBudgetedHoursFromTemplates } from './project-fields-step/use-budgeted-hours-from-template';
import {
  ProjectInfoStep,
  ProjectInfoStepFormValues,
} from './project-info-step';
import {
  getAllSources,
  getFieldFiltersFromTemplates,
  getPlaceholdersFromTemplates,
  getTemplates,
} from './project-info-step-utils';
import {
  ContentWrapper,
  FooterWrapper,
  FormWrapper,
  Header,
  HeadingWrapper,
  LeftColumn,
  Main,
  Modal,
  StyledProgress,
  Tab,
  TabsWrapper,
  Wrapper,
} from './styled';
import { ProjectData, ProjectPlaceholder } from './types';
import {
  createProjectPayload,
  isPartnerCompanyValue,
  fieldsToBeDisplayedInCreateProject,
} from './utils';
import { VendorTeamStep, VendorTeamStepFormValues } from './vendor-team-step';

export const isLastStep = (step: StepObject, steps: StepObject[]) => {
  const index = steps.indexOf(step);
  const { length } = steps;
  const lastIndex = length - 1;

  return index === lastIndex;
};

enum ProjectCreateSteps {
  ProjectInfo = 'project_info_step',
  FinancialsInfo = 'financials_info_step',
  VendorTeam = 'vendor_team_step',
  CustomerTeam = 'customer_team_step',
  ProjectFields = 'project_fields_step',
}

const getStepIcon = (stepId) => {
  switch (stepId) {
    case ProjectCreateSteps.ProjectInfo:
      return <BasicInfo16 />;
    case ProjectCreateSteps.FinancialsInfo:
      return <Money16 />;
    case ProjectCreateSteps.VendorTeam:
      return <UserMultiple16 />;
    case ProjectCreateSteps.CustomerTeam:
      return <Collaborate16 />;
    case ProjectCreateSteps.ProjectFields:
      return <Task16 />;
    default:
      return null;
  }
};

const ModalContent = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { user, company } = useUser();
  const { data: fields, isLoading } = useProjectCustomFields(
    fieldsToBeDisplayedInCreateProject,
  );
  const projectFieldsQueryResult = useProjectFieldsQuery();
  const { data: rolesWithPlaceholder } = useRolesWithPlaceholderQuery();
  const { data: fieldSections } = useProjectFieldSectionsQuery();
  const { byId: teamsById } = useSelector(({ teams }: RootState) => teams);
  const { data: partnerCompanies, isLoading: partnerCompaniesLoading } =
    useGetAllActivePartnerCompanies();

  const [customFields, setCustomFields] = useState<Field[]>([]);
  const [placeholders, setPlaceholders] = useState<[Team[], Team[]]>([[], []]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<UserOption[]>(
    [],
  );
  const [selectedCustomers, setSelectedCustomers] = useState<UserOption[]>([]);
  const [selectedTemplates, setSelectedTemplates] = useState<
    ProjectTemplateApi[]
  >([]);

  const projectStatusField = fields.find((field) => {
    return byFieldName(field, 'status');
  })!;
  const [projectData, setProjectData] = useState<ProjectData>({
    projectName: '',
    projectOwner: userOption(user),
    visibility: VisibilityType.ALL,
  });

  const [isTimesheetFeatureEnabled] = useAccountFeature(
    AccountFeature.Timesheets,
  );

  const { data: jiraConfig } = useQueryJiraConfig();

  const [fieldFilters, setFieldFilters] = useState(
    {} as Omit<SimpleSearchFilter, 'match'>,
  );

  useDeepCompareEffect(() => {
    setCustomFields(
      fields
        .filter(({ isEnabled }) => isEnabled)
        .filter((field) => {
          return !byFieldName(field, 'status');
        })
        .filter(
          (field) =>
            field.fieldType !== 'KEY_EVENTS' &&
            field.fieldType !== 'INTERVALS ',
        ),
    );
  }, [fields, setCustomFields]);

  const [projectInfoForm] = Form.useForm<ProjectInfoStepFormValues>();
  const [financialsInfoForm] = Form.useForm<FinancialInfoStepFormValues>();
  const [vendorTeamForm] = Form.useForm<VendorTeamStepFormValues>();
  const [customerTeamForm] = Form.useForm<CustomerTeamStepFormValues>();
  const [projectFieldsForm] = Form.useForm<ProjectFieldsStepFormValues>();

  const [isCreating, setIsCreating] = useState(false);
  const history = useHistory();
  const translate = useTranslate();

  const { mutate: createProject } = useCreateProjectMutation();
  const { mutateAsync: createCustomerChampionAsync } = useCreateUserMutation();
  const { mutateAsync: createCustomerUsersAsync } = useBulkUserCreateMutation();
  const { mutateAsync: createCustomerAsync } = useCreateCompanyMutation();

  const handleProjectInfoStep = async () => {
    projectInfoForm.submit();
  };
  const handleFinancialsInfoStep = async () => {
    financialsInfoForm.submit();
  };
  const handleVendorTeamStep = async () => {
    vendorTeamForm.submit();
  };
  const handleCustomerTeamStep = async () => {
    customerTeamForm.submit();
  };
  const handleProjectFieldsStep = async () => {
    projectFieldsForm.submit();
  };

  const validateProjectInfo = async () => {
    return projectInfoForm.validateFields();
  };
  const validateFinancialsInfo = async () => {
    return financialsInfoForm.validateFields();
  };
  const validateVendorTeam = async () => {
    return vendorTeamForm.validateFields();
  };
  const validateCustomerTeam = async () => {
    return customerTeamForm.validateFields();
  };
  const validateProjectFields = async () => {
    return projectFieldsForm.validateFields();
  };

  const handlers = {
    project_info_step: handleProjectInfoStep,
    financials_info_step: handleFinancialsInfoStep,
    vendor_team_step: handleVendorTeamStep,
    customer_team_step: handleCustomerTeamStep,
    project_fields_step: handleProjectFieldsStep,
  };

  const validators = {
    project_info_step: validateProjectInfo,
    financials_info_step: validateFinancialsInfo,
    vendor_team_step: validateVendorTeam,
    customer_team_step: validateCustomerTeam,
    project_fields_step: validateProjectFields,
  };

  const replaceNewUsersInPlaceholders = (
    users: User[],
    externalPlaceholders: ProjectPlaceholder[],
  ): ProjectPlaceholder[] => {
    return externalPlaceholders?.map((_placeholder) => {
      const { user: _user } = _placeholder;

      if (!_user?.__isNew__) {
        return _placeholder;
      }
      const _userObject = users.find((u) => u.emailId === _user.value);

      return {
        ..._placeholder,
        ...(_userObject && { user: userOption(_userObject) }),
      };
    });
  };

  const handleCreate = async () => {
    try {
      const projectFieldsstepValues: ProjectFieldsStepFormValues =
        await projectFieldsForm.validateFields();

      let tempFormData: ProjectData = {
        ...projectData,
        ...projectFieldsstepValues,
      };

      if (projectFieldsstepValues.fields) {
        const {
          fields: { _annualizedRecurringRevenue, _projectFee, ...restOfFieds },
          autoBudgetedHours,
          budgetedHours,
          ...restOfValues
        } = projectFieldsstepValues;

        const budgetedHoursPayload =
          autoBudgetedHours === true
            ? {
                autoBudgetedHours: true,
                budgetedHours: undefined,
              }
            : (autoBudgetedHours == null || autoBudgetedHours === false) &&
              budgetedHours != null
            ? {
                autoBudgetedHours: undefined,
                budgetedHours,
              }
            : {
                autoBudgetedHours: undefined,
                budgetedHours: undefined,
              };

        tempFormData = {
          ...projectData,
          ...restOfValues,
          ...budgetedHoursPayload,
          fields: restOfFieds,
          annualizedRecurringRevenue: _annualizedRecurringRevenue,
          projectFee: _projectFee,
        };
      }

      setIsCreating(true);
      if (projectData.customer?.__isNew__) {
        await createCustomerAsync(
          { companyName: projectData.customer.value.toString() },
          {
            onSuccess: async (customer) => {
              const result = { customer: companyOption(customer) };

              tempFormData = {
                ...tempFormData,
                ...result,
              };
              projectInfoForm.setFieldsValue(result);
            },
          },
        );
      }
      if (tempFormData.customer) {
        if (
          tempFormData.customerChampion &&
          tempFormData.customerChampion.__isNew__
        ) {
          await createCustomerChampionAsync(
            {
              emailId: tempFormData.customerChampion.value.toString(),
              company: { companyId: Number(tempFormData.customer.value) },
              userType: UserType.External,
            },
            {
              onSuccess: async (customerChampion) => {
                const result = {
                  customerChampion: {
                    ...userOption(customerChampion),
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    __isNew__: false,
                  } as UserOption,
                };

                tempFormData = {
                  ...tempFormData,
                  ...result,
                };
                vendorTeamForm.setFieldsValue(result);
                // updating the store
                dispatch(addUser(customerChampion));
              },
            },
          );
        }
        if (tempFormData.customer) {
          const companyId = Number(tempFormData.customer.value);
          const newUsers = (projectData.externalUsers ?? [])
            .filter((o) => o.__isNew__)
            .map((option) => ({
              emailId: option.value.toString(),
              company: { companyId },
              userType: UserType.External,
            }));

          if (newUsers.length) {
            await createCustomerUsersAsync(newUsers, {
              onSuccess: (users) => {
                const existingUsers = (projectData.externalUsers ?? []).filter(
                  (o) => !o.__isNew__,
                );

                const result = {
                  externalUsers: [...users.map(userOption), ...existingUsers],
                };

                tempFormData = {
                  ...tempFormData,
                  ...result,
                };

                const externalPlaceholders = customerTeamForm.getFieldValue(
                  'externalPlaceholders',
                );

                const _placeholderResult = {
                  externalPlaceholders: replaceNewUsersInPlaceholders(
                    users,
                    externalPlaceholders,
                  ),
                };

                tempFormData = {
                  ...tempFormData,
                  ..._placeholderResult,
                };
                customerTeamForm.setFieldsValue(_placeholderResult);

                vendorTeamForm.setFieldsValue(result);

                // updating the store
                users.forEach((u) => dispatch(addUser(u)));
              },
            });
          }
        }
      }
      const projectFields = projectFieldsQueryResult.data
        ? projectFieldsQueryResult.data
        : [];

      const {
        rateCardId,
        financialContractType,
        projectBudget,
        projectHours,
        ...projectCreateFormData
      } = tempFormData;

      let projectFinancialsDetailModel;

      if (financialContractType) {
        if (
          financialContractType.value !==
          ProjectFinancialsContractType.NONBILLABLE
        ) {
          projectFinancialsDetailModel = {
            financialContractType: financialContractType.value,
            projectBudget,
            ...(projectHours &&
              financialContractType.value !==
                ProjectFinancialsContractType.SUBSCRIPTION && {
                projectHours,
              }),
            ...(rateCardId && { rateCardId: rateCardId?.value }),
          };
        }

        if (
          financialContractType.value ===
          ProjectFinancialsContractType.NONBILLABLE
        ) {
          projectFinancialsDetailModel = {
            financialContractType: financialContractType.value,
            ...(projectHours ? { projectHours } : {}),
          };
        }
      }

      const projectFormPayload = {
        ...projectCreateFormData,
        ...(projectFinancialsDetailModel
          ? { projectFinancialsDetailModel }
          : {}),
      };

      const payload = createProjectPayload(projectFormPayload, projectFields);

      createProject(payload, {
        onSuccess: (response) => {
          history.push(getProjectPlanUrl(ProjectId(response.projectId)));
          queryClient.invalidateQueries(getTeamsInAccountQueryKey());
        },
        onSettled: () => {
          setIsCreating(false);
        },
      });
    } catch (e) {
      logger.error('Error: Project create failed', { error: e });
      setIsCreating(false);
    }
  };

  const handleValuesChange = async (changedValues, allValues) => {
    let values = allValues as ProjectData;

    if ('customer' in changedValues) {
      // whenever customer changes, Reset customer dependent fields
      vendorTeamForm.resetFields(['customerChampion', 'externalUsers']);
      values = {
        ...values,
        customerChampion: undefined,
        externalUsers: [],
      };
    }

    if ('source' in changedValues || 'sources' in changedValues) {
      const allSources = getAllSources(values);
      const templates = await getTemplates(
        allSources.map(({ value }) => value),
      );

      setSelectedTemplates(templates);

      if (isTimesheetFeatureEnabled) {
        const isBudgetedHourConfiguredForTemplate =
          anyTemplateHasBudgetedHoursConfig(templates);

        projectFieldsForm.setFieldsValue({
          autoBudgetedHours: isBudgetedHourConfiguredForTemplate
            ? true
            : undefined,
        });
      }

      setFieldFilters(getFieldFiltersFromTemplates(templates));
      setPlaceholders(
        getPlaceholdersFromTemplates({
          templates,
          rolesWithPlaceholder,
          teamsLookup: teamsById,
        }),
      );
      customerTeamForm.resetFields();

      projectInfoForm.resetFields(['dueDate']);
      values = {
        ...values,
        dueDate: undefined,
      };
    }

    if ('customerChampion' in changedValues) {
      vendorTeamForm.setFields([
        {
          name: 'customerChampion',
          errors: [],
        },
      ]);
    }

    if ('externalUsers' in changedValues) {
      vendorTeamForm.setFields([
        {
          name: 'externalUsers',
          errors: [],
        },
      ]);
      setSelectedCustomers(allValues.externalUsers);
    }

    if ('internalUsers' in changedValues) {
      setSelectedTeamMembers(allValues.internalUsers || []);
    }
    if ('internalPlaceholders' in changedValues) {
      const teamMembersList: { [id: number]: UserOption } = {};

      selectedTeamMembers.forEach((_user) => {
        teamMembersList[_user.value] = _user;
      });

      allValues.internalPlaceholders?.forEach((_placeholder) => {
        if (
          !_placeholder.user ||
          isPartnerCompanyValue(_placeholder.user.value) ||
          _placeholder.user.extra.userType === UserType.Partner
        ) {
          return;
        }
        teamMembersList[_placeholder.user.value] = _placeholder.user;
      });
      const _internalUsers = Object.values(teamMembersList);

      vendorTeamForm.setFieldsValue({
        internalUsers: _internalUsers,
      });
      values.internalUsers = _internalUsers;
    }
    if ('externalPlaceholders' in changedValues) {
      const customersList: { [id: number]: UserOption } = {};

      selectedCustomers?.forEach((_user) => {
        customersList[_user.value] = _user;
      });

      allValues.externalPlaceholders?.forEach((_placeholder) => {
        if (!_placeholder.user) {
          return;
        }
        customersList[_placeholder.user.value] = _placeholder.user;
      });
      const _externalUsers = Object.values(customersList);

      customerTeamForm.setFieldsValue({
        externalUsers: _externalUsers,
      });
      values.externalUsers = _externalUsers;
    }

    setProjectData({
      ...projectData,
      ...values,
    });
  };

  useEffect(() => {
    const internalPlaceholders = vendorTeamForm.getFieldValue(
      'internalPlaceholders',
    );

    if (!internalPlaceholders) {
      return;
    }
    const _internalPlaceholders = internalPlaceholders.map((_placeholder) => {
      const { user: _user, ...rest } = _placeholder;

      if (_user != null && !isPartnerCompanyValue(`${_user.value}`)) {
        if (
          _user?.extra?.userType !== UserType.Partner &&
          !selectedTeamMembers.find((u) => u.value === _user?.value)
        ) {
          return {
            ...rest,
            user: null,
          };
        }
      }

      return _placeholder;
    });

    vendorTeamForm.setFieldsValue({
      internalPlaceholders: _internalPlaceholders,
    });
  }, [vendorTeamForm, selectedTeamMembers]);

  useEffect(() => {
    const externalPlaceholders = customerTeamForm.getFieldValue(
      'externalPlaceholders',
    );

    if (!externalPlaceholders) {
      return;
    }
    const _externalPlaceholders = externalPlaceholders.map((_placeholder) => {
      const { user: _user, ...rest } = _placeholder;

      if (!selectedCustomers?.find((u) => u.value === _user?.value)) {
        return rest;
      }

      return _placeholder;
    });

    customerTeamForm.setFieldsValue({
      externalPlaceholders: _externalPlaceholders,
    });
  }, [customerTeamForm, selectedCustomers]);

  const getNextStepId = useCallback(
    (step: StepObject, steps: StepObject[], isFinancialsEnabled: boolean) => {
      // get the next step after financials, if it is not enabled
      const stepId =
        !isFinancialsEnabled && step.id === ProjectCreateSteps.ProjectInfo
          ? ProjectCreateSteps.FinancialsInfo
          : step.id;

      return isLastStep(step, steps)
        ? 'projects.create_project'
        : `projects.create.next_step.${stepId}`;
    },
    [],
  );

  const { data: templateDetails } = useTemplateDueDateQuery({
    startDate: projectData.startDate?.format(API_DATE_FORMAT),
    templateId: projectData.source?.sourceId,
  });

  const { maximumPossibleBudgetedHours } = useBudgetedHoursFromTemplates({
    templates: selectedTemplates,
  });

  useEffect(() => {
    financialsInfoForm.setFieldsValue({
      projectHours: maximumPossibleBudgetedHours,
    });
  }, [financialsInfoForm, maximumPossibleBudgetedHours]);

  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const [accountability] = useAccountFeature(AccountFeature.Accountability);
  const canAccessFinancials = accountability && isFinancialsEnabled;

  const renderItems = [
    {
      key: ProjectCreateSteps.ProjectInfo,
      Component: ProjectInfoStep,
      props: {
        form: projectInfoForm,
        onValuesChange: handleValuesChange,
        statusField: projectStatusField,
        projectData,
      },
      mandatoryFields: ['customer', 'projectName'],
    },
    {
      key: ProjectCreateSteps.FinancialsInfo,
      Component: FinancialInfoStep,
      props: {
        form: financialsInfoForm,
        onValuesChange: handleValuesChange,
      },
      mandatoryFields: ['financialContractType', 'projectBudget'],
      customValidator: (_projectData: ProjectData) => {
        if (
          _projectData.financialContractType &&
          _projectData.financialContractType.value !==
            ProjectFinancialsContractType.FIXED
        ) {
          return true;
        }

        if (
          _projectData.financialContractType?.value ===
            ProjectFinancialsContractType.FIXED &&
          _projectData.projectBudget
        ) {
          return true;
        }

        return false;
      },
    },
    {
      key: ProjectCreateSteps.VendorTeam,
      Component: VendorTeamStep,
      props: {
        form: vendorTeamForm,
        onValuesChange: handleValuesChange,
        projectData,
        internalPlaceholders: placeholders[0],
        partnerCompanies: partnerCompanies ?? [],
        templateDetails,
      },
    },
    {
      key: ProjectCreateSteps.CustomerTeam,
      Component: CustomerTeamStep,
      props: {
        externalPlaceholders: placeholders[1],
        form: customerTeamForm,
        onValuesChange: handleValuesChange,
        customer: projectInfoForm.getFieldValue('customer'),
        projectOwner: vendorTeamForm.getFieldValue('projectOwner'),
        externalUsers: selectedCustomers,
      },
    },
    {
      key: ProjectCreateSteps.ProjectFields,
      Component: ProjectFieldsStep,
      props: {
        templates: selectedTemplates,
        form: projectFieldsForm,
        fields: customFields,
        jiraConfig: jiraConfig && jiraConfig[0],
        fieldFilters,
        fieldSections,
      },
    },
  ];

  const finalRenderItems = renderItems.filter((item) =>
    canAccessFinancials ? true : item.key !== ProjectCreateSteps.FinancialsInfo,
  );

  const validateAndMoveToStep = (
    id: string,
    step: StepObject,
    steps: StepObject[],
    replace: (id) => void,
  ) => {
    const currentStepIndex = steps.indexOf(step);
    const nextStepIndex = steps.findIndex((_step) => _step.id === id);
    const isNextStep = nextStepIndex > currentStepIndex;

    if (isNextStep) {
      const stepsToValidate = steps.slice(currentStepIndex, nextStepIndex);

      Promise.allSettled(
        stepsToValidate.map((_step) => {
          const validator = validators[_step.id];

          return validator();
        }),
      ).then((results) => {
        const isValidated = results.every((e, index) => {
          const validateStep = stepsToValidate[index];

          const validateStepOptions = renderItems.find(
            (s) => s.key === validateStep.id,
          );
          const mandatoryFields = validateStepOptions?.mandatoryFields ?? [];
          const customValidator = validateStepOptions?.customValidator;

          if (e.status === 'fulfilled') {
            if (!mandatoryFields) {
              return true;
            }
            const isGood = customValidator
              ? customValidator(projectData)
              : mandatoryFields.every((field) => {
                  return !!projectData[field];
                });

            if (!isGood) {
              replace(validateStep.id);
              const validator = validators[validateStep.id];

              queueMacrotask(() => validator());

              return false;
            }
          }

          if (e.status === 'rejected') {
            replace(validateStep.id);

            return false;
          }

          return true;
        });

        if (isValidated) {
          replace(id);
        }
      });
    } else {
      replace(id);
    }
  };

  return isLoading || partnerCompaniesLoading ? (
    <PageLoading />
  ) : isCreating && projectData.customer ? (
    <Animate transitionName="fade" transitionAppear>
      <ProjectCreationLoader
        customer={projectData.customer}
        vendor={company}
        key="loader"
      />
    </Animate>
  ) : (
    <Wrapper wrap={false}>
      <LeftColumn>
        <Preview
          projectData={projectData}
          onValuesChange={handleValuesChange}
          vendorStepform={vendorTeamForm}
        />
      </LeftColumn>
      <Main>
        <Wizard
          render={({ step, steps, replace, next }) => {
            const currentStepIndex = steps.indexOf(step);
            const stepsSelected = steps.slice(0, currentStepIndex + 1);

            return (
              <ContentWrapper>
                <Header>
                  <HeadingWrapper>
                    {translate('projects.create.title')}
                  </HeadingWrapper>
                  <StyledProgress
                    strokeColor="var(--blue-60)"
                    trailColor="var(--gray-20)"
                    strokeLinecap="square"
                    percent={((steps.indexOf(step) + 1) / steps.length) * 100}
                    strokeWidth={2}
                    showInfo={false}
                  />
                  <TabsWrapper>
                    {steps
                      .filter((s) =>
                        canAccessFinancials
                          ? true
                          : s.id !== ProjectCreateSteps.FinancialsInfo,
                      )
                      .map((tabStep) => {
                        return (
                          <Tab
                            isSelected={stepsSelected.some(
                              (_step) => _step.id === tabStep.id,
                            )}
                            onClick={() =>
                              validateAndMoveToStep(
                                tabStep.id,
                                step,
                                steps,
                                replace,
                              )
                            }
                            key={tabStep.id}
                          >
                            {getStepIcon(tabStep.id)}
                            {translate(
                              `projects.step.tab.${tabStep.id}` as TranslationKey,
                            )}
                          </Tab>
                        );
                      })}
                  </TabsWrapper>
                </Header>

                <FormWrapper>
                  <Form.Provider
                    onFormFinish={(name, { values }) => {
                      setProjectData({ ...projectData, ...values });
                      if (isLastStep(step, steps)) {
                        handleCreate();
                      }
                      next();
                    }}
                  >
                    <Steps>
                      {finalRenderItems.map((item) => {
                        const { key, Component, props } = item;

                        return (
                          <Step id={key}>
                            <Component {...(props as any)} />
                          </Step>
                        );
                      })}
                    </Steps>
                  </Form.Provider>
                </FormWrapper>

                <FooterWrapper>
                  <Footer
                    handlers={handlers}
                    isSubmitting={isCreating}
                    getNextStepId={getNextStepId}
                  />
                </FooterWrapper>
              </ContentWrapper>
            );
          }}
        />
      </Main>
    </Wrapper>
  );
};

export const CreateProjectPage: FC = () => {
  return (
    <Modal centered visible width={1300} closable={false} footer={null}>
      <ModalContent />
    </Modal>
  );
};
